import { getAgeRangeLabel } from "./vaccineAgeRange";

/*
 * What the Add vaccine picker offers for a new dose (KIN-2127): the products that suit the patient
 * on the date given, grouped by vaccine type, or the reason there is nothing to pick yet.
 */

/**
 * The products on offer, plus the one already chosen when the age filter hides it. A product can be
 * chosen another way — a schedule prefill, a batch — and the picker must not show that choice blank.
 */
export const toPickerProducts = ({ products, selectedProduct }) => {
  const isSelectedHidden =
    !!selectedProduct &&
    !products.some((product) => String(product.id) === String(selectedProduct.id));

  return isSelectedHidden ? [...products, selectedProduct] : products;
};

/**
 * A product's name, with its licensed age range once the age filter has been lifted — unless the
 * name already says it, as "Comirnaty Omicron KP.2 (12 years and over)" does.
 */
const toProductLabel = ({ product, isShowingAll }) => {
  const name = product.name ?? "";
  const ageRange = isShowingAll ? getAgeRangeLabel(product) : null;

  return ageRange && !name.includes(ageRange) ? `${name} (${ageRange})` : name;
};

const byLabel = (first, second) => first.label.localeCompare(second.label);

/** The products grouped by vaccine type, each group and each product in alphabetical order. */
export const toProductGroups = ({ products, isShowingAll = false }) => {
  const productsByType = products.reduce((acc, product) => {
    const group = product.vaccineType || "Other";

    if (!acc[group]) acc[group] = [];
    acc[group].push({
      label: toProductLabel({ product, isShowingAll }),
      value: String(product.id),
    });

    return acc;
  }, {});

  return Object.entries(productsByType)
    .map(([label, options]) => ({ label, options: options.sort(byLabel) }))
    .sort(byLabel);
};

/**
 * The reason there is nothing to pick yet, shown as the picker's placeholder so it reads without
 * opening the list. Null once the products are there.
 */
export const getPickerNotice = ({
  patientId,
  dateGiven,
  isLoading,
  isError,
  isPatientNotInPractice,
}) => {
  if (!patientId) return "Choose the patient first";

  if (!dateGiven) return "Enter the date given first";

  if (isLoading) return "Loading vaccines...";

  if (isPatientNotInPractice) {
    return "This patient is not registered with this practice";
  }

  if (isError) return "Could not load vaccines. Try again later.";

  return null;
};
